/**
 * Propositions (§10, §12, §14): validation, contradiction as a lookup, and
 * distortion as a field perturbation. See `types/proposition.ts`.
 *
 * Nothing here reads prose. A claim is a triple; two claims contradict when
 * they share subject, predicate and time and differ in object; a claim
 * contradicts the record when an event says otherwise. That is the whole
 * of "you told me you weren't there," and it costs a filter.
 */

import { entityTag } from './ids.js';
import type { Rng } from './rng.js';
import type {
  Actor,
  Contradiction,
  DistortionRule,
  EntityRef,
  Event,
  Fact,
  NpcId,
  Predicate,
  Proposition,
  PropositionValue,
  TimeRef,
  WorldState,
} from './types/index.js';
import { refExists } from './validate.js';

export const PREDICATES: readonly Predicate[] = [
  'was_at',
  'did',
  'owes',
  'works_for',
  'bonded_to',
  'moved_against',
  'will_be_at',
  'is_dead',
  'said',
  'knows',
];

export function isPredicate(value: unknown): value is Predicate {
  return typeof value === 'string' && (PREDICATES as readonly string[]).includes(value);
}

export function isTimeRef(value: unknown): value is TimeRef {
  if (typeof value !== 'object' || value === null) return false;
  const t = value as { kind?: unknown; day?: unknown; from?: unknown; to?: unknown };
  if (t.kind === 'day') return Number.isInteger(t.day);
  if (t.kind === 'range') return Number.isInteger(t.from) && Number.isInteger(t.to) && (t.from as number) <= (t.to as number);
  return false;
}

function isValue(state: WorldState, value: unknown, depth: number): value is PropositionValue {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as { kind?: unknown; amount?: unknown; type?: unknown; proposition?: unknown };
  if (v.kind === 'money') return typeof v.amount === 'number' && Number.isFinite(v.amount) && v.amount >= 0;
  if (v.kind === 'event_type') return typeof v.type === 'string' && v.type.length > 0;
  if (v.kind === 'proposition') return depth < 1 && validateProposition(state, v.proposition, depth + 1) === null;
  return false;
}

/** Null when well-formed over things that exist; otherwise why not. */
export function validateProposition(state: WorldState, value: unknown, depth = 0): string | null {
  if (typeof value !== 'object' || value === null) return 'proposition is not an object';
  const p = value as Partial<Proposition>;
  if (!refExists(state, p.subject)) return 'subject does not exist';
  if (!isPredicate(p.predicate)) return 'predicate is not in the closed set';
  if (!refExists(state, p.object) && !isValue(state, p.object, depth)) return 'object is neither an entity nor a value';
  if (p.timeRef !== null && !isTimeRef(p.timeRef)) return 'timeRef is malformed';
  return null;
}

function timeKey(t: TimeRef | null): string {
  if (t === null) return '*';
  return t.kind === 'day' ? `d${t.day}` : `r${t.from}-${t.to}`;
}

/** What makes two claims about the same thing: subject, predicate, time. */
export function propositionKey(p: Proposition): string {
  return `${entityTag(p.subject)}|${p.predicate}|${timeKey(p.timeRef)}`;
}

export function objectTag(o: EntityRef | PropositionValue): string {
  switch (o.kind) {
    case 'money':
      return `money:${o.amount}`;
    case 'event_type':
      return `event_type:${o.type}`;
    case 'proposition':
      return `prop:${propositionKey(o.proposition)}=${objectTag(o.proposition.object)}`;
    default:
      return entityTag(o);
  }
}

export function sameProposition(a: Proposition, b: Proposition): boolean {
  return propositionKey(a) === propositionKey(b) && objectTag(a.object) === objectTag(b.object);
}

function overlaps(a: TimeRef | null, b: TimeRef | null): boolean {
  if (a === null || b === null) return true;
  const [af, at] = a.kind === 'day' ? [a.day, a.day] : [a.from, a.to];
  const [bf, bt] = b.kind === 'day' ? [b.day, b.day] : [b.from, b.to];
  return af <= bt && bf <= at;
}

/** The record's own version of what an event says, for predicates it can speak to. */
export function propositionsOf(event: Event): Proposition[] {
  const out: Proposition[] = [];
  const subject: EntityRef = event.actor === 'player' ? { kind: 'player' } : { kind: 'npc', id: event.actor };
  const when: TimeRef = { kind: 'day', day: event.at.day };
  if (event.location !== null) out.push({ subject, predicate: 'was_at', object: { kind: 'location', id: event.location }, timeRef: when });
  out.push({ subject, predicate: 'did', object: { kind: 'event_type', type: event.type }, timeRef: when });
  if (event.type === 'death' && event.target?.kind === 'npc') {
    out.push({ subject: event.target, predicate: 'is_dead', object: { kind: 'event_type', type: 'death' }, timeRef: when });
  }
  return out;
}

/**
 * Claims by `by` that conflict with what `listener` has reason to believe:
 * other claims made to them, and events they took part in or witnessed.
 * A claim is only ever contradicted by a different object on the same key
 * (§12: lies must be discoverable, and a lie you can never catch is noise).
 */
export function contradictions(state: WorldState, by: Actor, listener: Actor): Contradiction[] {
  const out: Contradiction[] = [];
  const claims = Object.values(state.memory.facts).filter((f): f is Extract<Fact, { kind: 'claim' }> => f.kind === 'claim');
  const mine = claims.filter((c) => c.by === by && c.to === listener);
  const heard = claims.filter((c) => c.to === listener && c.by !== by);
  const seen = state.memory.events.filter(
    (e) => e.actor === listener || e.witnesses.includes(listener) || (e.target?.kind === (listener === 'player' ? 'player' : 'npc') && (listener === 'player' || (e.target.kind === 'npc' && e.target.id === listener))),
  );

  for (const claim of mine) {
    const key = propositionKey(claim.proposition);
    for (const other of heard) {
      if (propositionKey(other.proposition) === key && objectTag(other.proposition.object) !== objectTag(claim.proposition.object)) {
        out.push({ claim: claim.proposition, claimedBy: by, against: { kind: 'claim', proposition: other.proposition, by: other.by } });
      }
    }
    for (const event of seen) {
      for (const truth of propositionsOf(event)) {
        const sameSubject = entityTag(truth.subject) === entityTag(claim.proposition.subject);
        if (!sameSubject || truth.predicate !== claim.proposition.predicate) continue;
        if (!overlaps(truth.timeRef, claim.proposition.timeRef)) continue;
        // was_at: a different place on the same day is a contradiction.
        if (truth.predicate === 'was_at' && objectTag(truth.object) !== objectTag(claim.proposition.object)) {
          out.push({ claim: claim.proposition, claimedBy: by, against: { kind: 'event', eventId: event.id, proposition: truth } });
        }
      }
    }
  }
  return out;
}

/**
 * Rumor distortion (§10) as a perturbation of one field. Seeded, so the
 * same rumor reaches the same ear the same way every run.
 */
export function distort(state: WorldState, p: Proposition, rule: DistortionRule, rng: Rng): Proposition {
  switch (rule) {
    case 'wrong_participant': {
      const others = Object.values(state.npcs)
        .map((n) => n.id)
        .filter((id) => !(p.subject.kind === 'npc' && p.subject.id === id));
      if (others.length === 0) return p;
      const id: NpcId = rng.pick(others);
      return { ...p, subject: { kind: 'npc', id } };
    }
    case 'wrong_reason': {
      const others = PREDICATES.filter((pr) => pr !== p.predicate && pr !== 'said');
      return { ...p, predicate: rng.pick(others) };
    }
    case 'wrong_date': {
      const shift = rng.pick([-3, -2, -1, 1, 2, 3]);
      if (p.timeRef === null) return { ...p, timeRef: { kind: 'day', day: state.time.day + shift } };
      if (p.timeRef.kind === 'day') return { ...p, timeRef: { kind: 'day', day: p.timeRef.day + shift } };
      return { ...p, timeRef: { kind: 'range', from: p.timeRef.from + shift, to: p.timeRef.to + shift } };
    }
  }
}
